import React, { useState, useCallback } from 'react'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { IEcommerceAnswerSchema, IEcommerceProductGallery } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import style from './ZoomableImage.module.scss'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { getBigAssetPath } from '@aytm/lib/common/assets.ts'

interface IZoomableImageProps {
  asset: IImageAsset | IEcommerceProductGallery
  appearence: 'small' | 'main'
  answer: IEcommerceAnswerSchema
  assetIndex: number
}

const ZoomableImage = ({ asset, appearence, answer, assetIndex }: IZoomableImageProps) => {
  const { t } = useSurveyLocale()
  const [position, setPosition] = useState<null | { x: number; y: number }>(null)
  const [onImage, setOnImage] = useState<boolean>(false)
  const timeline = useTimeline()

  const onMoveOnImage = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onImage) return
      const element = event.currentTarget
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        const centerX = width / 2
        const centerY = height / 2
        const mouseX = event.clientX - left
        const mouseY = event.clientY - top

        setPosition({
          x: -(mouseX - centerX) * 0.75,
          y: -(mouseY - centerY) * 0.75
        })
      })
    },
    [onImage]
  )

  const onClickOnImage = () => {
    if (!onImage) {
      timeline.log({ ref: answer.id, target: assetIndex.toString(), type: 'zoom_image_in' })
      setOnImage(true)
    }
  }

  const onLeaveImage = () => {
    requestAnimationFrame(() => {
      if (onImage) {
        timeline.log({ ref: answer.id, target: assetIndex.toString(), type: 'zoom_image_out' })
      }
      setOnImage(false)
      setPosition({ x: 0, y: 0 })
    })
  }

  return (
    <div
      className={`${style.imageWrapper} ${style[appearence]} ${onImage ? style.imageWrapperActive : ''}`}
      onClick={onClickOnImage}
      onMouseLeave={onLeaveImage}
    >
      <div
        className={style.image}
        role="img"
        aria-label={asset.alt_text || t('questions', 'img', 'noAlt')}
        style={{
          backgroundImage: `url(${getBigAssetPath(asset)})`,
          backgroundPosition: position ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)` : '50% 50%',
          transform: `scale(${onImage ? 2 : 1})`,
          cursor: `${onImage ? 'zoom-out' : 'zoom-in'}`
        }}
        onMouseMove={onMoveOnImage}
      />
    </div>
  )
}

export default ZoomableImage
